import { useState, useRef, useEffect } from 'react';
import { get } from 'lodash';
import { createWidgetSubmission, uploadFile, fetchFileUploadStatus } from '../actions';

/**
 * Custom hook to handle file uploads with polling for scan status
 * @param {Object} coreToken - The core token object with token
 * @returns {Object} File upload handlers and state
 */
const useFileUpload = (coreToken) => {
  const recordType = 'WidgetSubmission';
  const pollingInterval = 5000;

  const [hasPendingFiles, setHasPendingFiles] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const pollingIntervalRef = useRef(null);
  const fileUploadStatusesRef = useRef({});
  const widgetSubmissionIdRef = useRef(null);
  const uploadedFilesRef = useRef(new Set());

  // Generate a unique identifier for a file
  const getFileId = (file) => {
    return `${file.name}-${file.size}-${file.lastModified}`;
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const startPolling = (token, submissionId) => {
    // Don't start if already polling
    if (pollingIntervalRef.current) {
      return;
    }

    // Check if there are any pending files
    const hasPending = Object.values(fileUploadStatusesRef.current).some(
      (status) => status === 'pending'
    );

    if (!hasPending) {
      stopPolling();
      return;
    }

    const checkFileStatuses = async () => {
      try {
        const statusResponse = await fetchFileUploadStatus(
          token,
          recordType,
          submissionId
        );

        if (!statusResponse.error && statusResponse.data) {
          const data = get(statusResponse, 'data.data', []);
          const newStatuses = {};

          data.forEach((item) => {
            newStatuses[item.id] = get(item, 'attributes.status');
          });

          // Update ref
          fileUploadStatusesRef.current = newStatuses;
          const hasPendingStatus = Object.values(fileUploadStatusesRef.current).some(
            (status) => status === 'pending'
          );
          setHasPendingFiles(hasPendingStatus);

          // If no pending files, stop polling
          if (!hasPendingStatus) {
            stopPolling();
          }
        }
      } catch (error) {
        console.error('Error checking file upload statuses', error);
      }
    };

    // Poll immediately and then at interval
    checkFileStatuses();
    pollingIntervalRef.current = setInterval(checkFileStatuses, pollingInterval);
  };

  const handleFileSelect = async (fileOrFiles) => {
    if (!coreToken) {
      console.error('Core token not available');
      return;
    }

    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];

    // Filter out files that have already been uploaded
    const newFiles = files.filter((file) => {
      if (!file) return false;
      const fileId = getFileId(file);
      return !uploadedFilesRef.current.has(fileId);
    });

    if (newFiles.length === 0) {
      console.log('All selected files have already been uploaded');
      return;
    }

    setIsUploadingFiles(true);

    try {
      // If this is the first file, create a WidgetSubmission first
      let widgetSubmissionId = widgetSubmissionIdRef.current;

      if (!widgetSubmissionId) {
        const submissionResponse = await createWidgetSubmission(coreToken);

        if (submissionResponse.error || !submissionResponse.data) {
          console.error('Failed to create widget submission', submissionResponse);
          return;
        }

        widgetSubmissionId = get(submissionResponse, 'data.data.id');

        if (!widgetSubmissionId) {
          console.error(
            'Widget submission ID not found in response',
            submissionResponse
          );
          return;
        }

        widgetSubmissionIdRef.current = widgetSubmissionId;
      }

      // Upload only new files
      const uploadPromises = newFiles.map((file) =>
        uploadFile(coreToken, file, widgetSubmissionId, recordType)
      );

      const uploadResponses = await Promise.all(uploadPromises);

      uploadResponses.forEach((response, index) => {
        if (!response.error && response.data) {
          const fileId = getFileId(newFiles[index]);
          uploadedFilesRef.current.add(fileId);

          // Store the file upload ID from the response
          const uploadId = get(response, 'data.data.id', null);
          if (uploadId) {
            // Initialize status as pending
            fileUploadStatusesRef.current = {
              ...fileUploadStatusesRef.current,
              [uploadId]: 'pending',
            };
            setHasPendingFiles(true);
          }
        }
      });

      const failedUploads = uploadResponses.filter(
        (response) => response.error || !response.data
      );

      if (failedUploads.length > 0) {
        console.error('Some files failed to upload', failedUploads);
      } else {
        console.log(`Successfully uploaded ${newFiles.length} file(s)`);
      }
    } catch (error) {
      console.error('Error handling file upload', error);
    } finally {
      setIsUploadingFiles(false);
      startPolling(coreToken, widgetSubmissionIdRef.current);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const getWidgetSubmissionId = () => widgetSubmissionIdRef.current;

  return {
    handleFileSelect,
    hasPendingFiles,
    isUploadingFiles,
    getWidgetSubmissionId,
  };
};

export default useFileUpload;
